import { Button, Group, Paper } from '@mantine/core';
import { IconCheck, IconFileDownload } from '@tabler/icons-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { LotsColumnPresetControl } from './lots-column-preset-control';
import { LotsColumnPreset } from './lots-column-preset';
import { LotsFilterToolbar } from './lots-filter-toolbar';
import { LotsStatsHeader } from './lots-stats-header';
import classes from './lots-validation.module.css';

export interface LotsValidationHeaderProps {
  columnPreset: LotsColumnPreset;
  onColumnPresetChange: (preset: LotsColumnPreset) => void;
  total: number | undefined;
  pending: number | undefined;
  validated: number | undefined;
  statsLoading?: boolean;
  years: number[];
  species: { value: string; label: string }[];
  selectedCount?: number;
  onValidateSelected?: () => void;
  isValidateSelectedLoading?: boolean;
  onExport?: () => void;
  isExportLoading?: boolean;
}

export const LotsValidationHeader: FC<LotsValidationHeaderProps> = ({
  columnPreset,
  onColumnPresetChange,
  total,
  pending,
  validated,
  statsLoading,
  years,
  species,
  selectedCount = 0,
  onValidateSelected,
  isValidateSelectedLoading,
  onExport,
  isExportLoading,
}) => {
  const { t } = useTranslation();

  const validateButton = onValidateSelected ? (
    <Button
      disabled={selectedCount === 0}
      leftSection={<IconCheck size={16} />}
      loading={isValidateSelectedLoading}
      onClick={onValidateSelected}
      size="compact-sm"
    >
      {t('lots.validate_selection', { count: selectedCount })}
    </Button>
  ) : null;

  return (
    <Paper p="sm" withBorder={false}>
      <div className={classes.header}>
        <div className={classes.topRow}>
          <h1 className={classes.pageTitle}>{t('lots.page_title')}</h1>
          <LotsStatsHeader
            isLoading={statsLoading}
            pending={pending}
            total={total}
            validated={validated}
          />
        </div>

        <LotsFilterToolbar species={species} years={years} />

        <div className={classes.controlsRow}>
          <LotsColumnPresetControl
            onChange={onColumnPresetChange}
            value={columnPreset}
          />
          <Group gap="sm">
            {onExport ? (
              <Button
                leftSection={<IconFileDownload size={16} />}
                loading={isExportLoading}
                onClick={onExport}
                size="compact-sm"
                variant="default"
              >
                {t('common.export')}
              </Button>
            ) : null}
            {validateButton}
          </Group>
        </div>
      </div>
    </Paper>
  );
};

export default LotsValidationHeader;
